import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import MobileBottomNav from './MobileBottomNav';
import EmailVerifyBanner from './EmailVerifyBanner';
import TrustBadge from './TrustBadge';
import { DashboardHeader } from './dashboard/DashboardComponents';
import { CaretLeft } from '@phosphor-icons/react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_moneyswap-9/artifacts/pvoha9c5_D%24.png";

/**
 * Shared layout for all authenticated pages in the new chunky-pill style.
 * - Mobile: dark, hamburger top-right, logo top-left, bottom nav
 * - Desktop: full DashboardHeader at top
 * - back: if true, shows a left-arrow back button instead of hamburger on mobile
 */
export default function PageShell({ children, back = false, title = null, testId }) {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { t, isDark } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => { await logout(); navigate('/login'); };

  return (
    <div
      className="min-h-screen"
      style={{ background: t.pageBg, color: t.textPrimary }}
      data-testid={testId}
      data-theme={isDark ? 'dark' : 'light'}
    >
      <div className="hidden md:block">
        <DashboardHeader mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} onLogout={handleLogout} />
      </div>

      <EmailVerifyBanner />

      <header
        className="md:hidden px-5 pt-6 pb-2 flex items-center justify-between"
        style={{ background: t.pageBg }}
      >
        {back ? (
          <button onClick={() => navigate(-1)} className="p-1" aria-label="Back" data-testid="page-back-btn" style={{ color: t.textPrimary }}>
            <CaretLeft className="w-6 h-6" />
          </button>
        ) : (
          <Link to="/dashboard">
            <img
              src={LOGO_URL}
              alt="Divispend"
              className="h-12 w-auto"
              style={{ filter: isDark ? 'none' : 'invert(1) hue-rotate(180deg)' }}
            />
          </Link>
        )}
        {back && (
          <img
            src={LOGO_URL}
            alt="Divispend"
            className="h-10 w-auto"
            style={{ filter: isDark ? 'none' : 'invert(1) hue-rotate(180deg)' }}
          />
        )}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="p-2"
          aria-label="Menu"
          style={{ color: t.textPrimary }}
        >
          <div className="space-y-1.5">
            <span className="block w-7 h-0.5" style={{ background: t.textPrimary }} />
            <span className="block w-7 h-0.5" style={{ background: t.textPrimary }} />
            <span className="block w-7 h-0.5" style={{ background: t.textPrimary }} />
          </div>
        </button>
      </header>

      <main className="max-w-5xl mx-auto px-5 pt-4 pb-32 md:pb-12">
        {title && (
          <div className="mb-6">
            <h1
              className="text-3xl sm:text-4xl font-extrabold tracking-tight leading-none"
              style={{ fontFamily: 'Cabinet Grotesk', color: t.textPrimary }}
            >
              {title.line1}
            </h1>
            {title.line2 && (
              <p
                className="text-xl sm:text-2xl font-bold tracking-tight mt-1.5"
                style={{ fontFamily: 'Cabinet Grotesk', color: t.accent }}
              >
                {title.line2}
              </p>
            )}
          </div>
        )}
        {children}

        <div className="mt-12 mb-2 flex justify-center md:justify-start">
          <TrustBadge />
        </div>
      </main>

      <MobileBottomNav />
    </div>
  );
}
